import { _js } from '@ifixit/localize';
import { useEffect } from 'react';
import DevicePickerService from 'Shared/device_picker_service';
import WorkbenchManager from 'Shared/workbench_manager';
import WorkbenchItem from 'Shared/workbench_item';
import styled from 'styled-components';
import { color, space, fontSize } from '@core-ds/primitives';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { DevicePickerModal } from 'Shared/DevicePickerModal';
import API_2_0 from 'Shared/api_2_0';
import * as HeaderActions from 'Shared/Header/actions';
import { store } from 'Shared/react-initialize';
import { Button, useDisclosure } from '@chakra-ui/react';

const WorkbenchContainer = styled.div`
   width: 350px;
   text-align: left;
   padding: ${space[0]};
   flex: 1;
   overflow: hidden;
`;

const WorkbenchHeaderContainer = styled.div`
   padding: ${space[4]} ${space[0]};
   background-color: ${color.gray[50]};
   text-align: center;
`;

const WorkbenchHeader = styled.span`
   text-transform: uppercase;
   font-weight: 700;
   font-size: ${fontSize.lg};
   line-height: 1;
   color: ${color.gray[600]};
`;

const WorkbenchTopicContainer = styled.div`
   max-height: 40vh;
   overflow: auto;
   background-color: ${color.white};
`;

const WorkbenchEmptyMessage = styled.p`
   padding: ${space[5]} ${space[7]};
   margin: ${space[0]};
   font-size: ${fontSize.lg};
   text-align: center;
`;

const WorkbenchAddTopicContainer = styled.div`
   width: 100%;
   height: 72px;
   padding: ${space[4]};
   background-color: ${color.gray[50]};
`;

const WorkbenchAddTopicButton = styled.button`
   width: 100%;
`;

const TopicList = ({ topics }) => {
   return topics.map(topic => (
      <WorkbenchItem key={`${topic.wikiid}-${topic.variantid}`} topic={topic} />
   ));
};

const EmptyTopicsMessage = () => {
   return (
      <WorkbenchEmptyMessage id="workbench-empty-message">
         {_js('Get quick access to guides, parts and answers for your devices')}
      </WorkbenchEmptyMessage>
   );
};

const Workbench = ({ topics, onTippyToggle }) => {
   useEffect(WorkbenchManager.getCurrentWorkbench, []);
   const newDevicePicker = App.lang === 'en';

   const onDeviceSelect = device => {
      WorkbenchManager.addItemWithVariantString(
         device.variant || null,
         /* showIndicator */ true,
         device.device
      );
      const chosenDeviceString = device.device + (device.variant ? ` (${device.variant})` : '');
      trackInPiwikAndGA({
         eventCategory: 'Workbench',
         eventAction: `Workbench - Device Picker "Add a Device" - ${chosenDeviceString} - Choose`,
      });
   };

   const selectDevice = async () => {
      const selectedDeviceTitle = await DevicePickerService.selectDevice({
         allowOrphan: false,
         initialView: 'COLUMN',
      });

      const newDevice = {
         device: selectedDeviceTitle,
         variant: null,
      };

      // Open Workbench after selecting a device.
      document.getElementById('workbenchDropdownToggle').parentNode.click();

      onDeviceSelect(newDevice);
   };

   // if the workbench does not receive topics (loading) do not render anything
   if (!topics) {
      return <WorkbenchContainer id="loading-workbench"></WorkbenchContainer>;
   }

   const hasTopics = topics.length !== 0;
   const Topics = hasTopics ? TopicList : EmptyTopicsMessage;

   return (
      <WorkbenchContainer id="workbench">
         <WorkbenchHeaderContainer>
            <WorkbenchHeader>{_js('My Workbench')}</WorkbenchHeader>
         </WorkbenchHeaderContainer>
         <WorkbenchTopicContainer id="workbench-topic-container">
            <Topics topics={topics} />
         </WorkbenchTopicContainer>
         <WorkbenchAddTopicContainer>
            {newDevicePicker ? (
               <WorkbenchDevicePickerModal
                  addToWorkbench={onDeviceSelect}
                  devicesInWorkbench={topics}
                  onTippyToggle={onTippyToggle}
               />
            ) : (
               <WorkbenchAddTopicButton
                  data-test-id="workbench-add-device"
                  className="button button-action-solid"
                  onClick={selectDevice}
               >
                  {_js('Add a Device')}
               </WorkbenchAddTopicButton>
            )}
         </WorkbenchAddTopicContainer>
      </WorkbenchContainer>
   );
};

export default Workbench;

const WorkbenchDevicePickerModal = ({ addToWorkbench, devicesInWorkbench, onTippyToggle }) => {
   const { isOpen, onOpen, onClose } = useDisclosure();

   const selectDevice = device => {
      if (isInWorkbench(device, devicesInWorkbench)) {
         removeDeviceFromWorkbench(device.device, device.variant);
      } else {
         addToWorkbench(device);
      }
   };

   const removeDeviceFromWorkbench = (deviceTitle, variantString) => {
      const request = new API_2_0('user/workbench/removeWithVariantString', {
         method: 'POST',
      })
         .send({
            device: deviceTitle,
            variant: variantString || null,
         })
         .then(response => {
            store.dispatch(HeaderActions.updateWorkbench(response.topics));
         });
      const removedDeviceString = deviceTitle + (variantString ? ` (${variantString})` : '');
      trackInPiwikAndGA({
         eventCategory: 'Workbench',
         eventAction: `Workbench - Device Picker - ${removedDeviceString} - Remove`,
      });
      LoadingIndicator.withPromise(request, {
         pendingMessage: _js('Removing'),
         successMessage: _js('Removed'),
      });
   };

   const openModal = () => {
      onTippyToggle();
      onOpen();
      trackInPiwikAndGA({
         eventCategory: 'Workbench',
         eventAction: 'Workbench - Btn "Add a Device" - Click',
      });
   };

   const closeModal = () => {
      onTippyToggle();
      onClose();
      trackInPiwikAndGA({
         eventCategory: 'Workbench',
         eventAction: 'Workbench - Device Picker - Close',
      });
   };

   return (
      <>
         <Button
            data-test-id="workbench-add-device"
            variant="cta"
            fontWeight="semibold"
            borderRadius="4px"
            width="100%"
            py={2}
            height="38px"
            onClick={openModal}
         >
            {_js('Add a Device')}
         </Button>
         <DevicePickerModal
            isOpen={isOpen}
            onClose={closeModal}
            onSelect={selectDevice}
            devicesInWorkbench={devicesInWorkbench}
            titleInstruction={_js('Select a model')}
            selectionProps={'allowSelectingVariants'}
         />
      </>
   );
};

function isInWorkbench(device, devicesInWorkbench) {
   return devicesInWorkbench.some(wbDevice => {
      const wikiidMatches = wbDevice.wikiid === device.wikiid;
      const variantidMatches = device.variantid === wbDevice.variantid;
      const noVariantIds = !device.variantid && !wbDevice.variantid;
      return wikiidMatches && (variantidMatches || noVariantIds);
   });
}
